import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme as MuiTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

/**
 * TypeScript interface for the Theme Context
 * Defines the shape of the context data and available methods
 */
export interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  theme: MuiTheme;
}

// Create the context with undefined default value
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Light theme configuration
 * Uses green as primary color and amber as secondary to match agricultural theme
 * Now includes info, warning, error, neutral, highlight, and backgroundAccent colors for consistency with Tailwind
 */
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2E7D32', // Green color for the farm theme
    },
    secondary: {
      main: '#FFA000', // Amber color for contrast
    },
    info: {
      main: '#0288D1', // Info blue
    },
    warning: {
      main: '#FFC107', // Warning yellow
    },
    error: {
      main: '#D32F2F', // Error red
    },
    success: {
      main: '#00C853', // Highlight green
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#222',
      secondary: '#607D8B', // Neutral gray
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
        },
      },
    },
  },
});

/**
 * Dark theme configuration
 * Uses brighter green and amber colors for better visibility in dark mode
 * Now includes info, warning, error, neutral, highlight, and backgroundAccent colors for consistency with Tailwind
 */
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#66BB6A',
      light: '#A5D6A7',
      dark: '#2E7D32',
      contrastText: '#0B0B0B',
    },
    secondary: {
      main: '#FFCA28',
      light: '#FFE082',
      dark: '#F57C00',
      contrastText: '#0B0B0B',
    },
    info: {
      main: '#4FC3F7',
      light: '#B3E5FC',
      dark: '#0288D1',
      contrastText: '#0B0B0B',
    },
    warning: {
      main: '#FFD54F',
      light: '#FFECB3',
      dark: '#FF8F00',
      contrastText: '#0B0B0B',
    },
    error: {
      main: '#EF5350',
      light: '#FFCDD2',
      dark: '#C62828',
      contrastText: '#ffffff',
    },
    success: {
      main: '#66BB6A',
      light: '#C8E6C9',
      dark: '#2E7D32',
      contrastText: '#0B0B0B',
    },
    background: {
      default: '#0E0F10',
      paper: '#17191B',
    },
    text: {
      primary: '#F4F6F8',
      secondary: '#B4BCC4',
      disabled: '#6B7280',
    },
    action: {
      hover: 'rgba(255,255,255,0.06)',
      selected: 'rgba(102,187,106,0.16)',
      hoverOpacity: 0.06,
      disabledBackground: 'rgba(255,255,255,0.06)',
    },
    divider: 'rgba(255,255,255,0.10)',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 4,
        },
      },
    },
  },
});

/**
 * Props interface for the ThemeProvider component
 */
interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * ThemeProvider component that manages theme state and provides theme context
 * Handles persistence of theme preference in localStorage
 * Provides theme toggling functionality
 * 
 * @param {ThemeProviderProps} props - Component props
 * @returns {JSX.Element} Provider component with theme context
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  /**
   * Determines the initial theme based on localStorage or system preference
   * @returns {boolean} Whether dark mode should be initially enabled
   */
  const getInitialTheme = (): boolean => {
    try {
      const savedTheme = localStorage.getItem('darkMode');
      if (savedTheme !== null) {
        return Boolean(JSON.parse(savedTheme));
      }
    } catch {
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // State for tracking dark mode preference
  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme());
  const activeTheme = isDarkMode ? darkTheme : lightTheme;

  /**
   * Toggles between light and dark theme
   */
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Save theme preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));

    // Update the HTML class for Tailwind dark mode
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Create context value to be provided
  const themeContextValue: ThemeContextType = {
    isDarkMode,
    toggleTheme,
    theme: activeTheme,
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={activeTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook for accessing the theme context
 * Makes it easier to use theme data and methods throughout the application
 * 
 * @returns {ThemeContextType} The theme context data and methods
 * @throws {Error} If used outside of a ThemeProvider
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 
